import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit'

import { serializeAggregatedTrack } from '@disco/api/utils/aggregated-search-track'
import { TRACKS_UPDATE } from '@disco/constants/ActionTypes'
import { NESTED_TRACKS_IN_SEARCH } from '@disco/constants/FeatureTypes'
import { fetchNestedTracks } from '@disco/state-management/actions/PlaylistTrackActions'
import { getConstanceSetting } from '@disco/state-management/selectors/user'
import { normalizeV2Track } from '@disco/utils/search-page'
import { getNestedTracksCount } from '@disco/utils/tracks'

import type { Group } from 'types/group'
import type { State } from 'types/redux'
import type { Track } from 'types/track'

type SearchThunk<Result> = ThunkAction<Result, State, unknown, UnknownAction>

/** Normalizes V2 track hits and hydrates their shared track and nesting state. */
export const hydrateV2SearchTracks =
  (hits: Array<Track | Group>): SearchThunk<Track[]> =>
  (dispatch, getState) => {
    const tracks = hits
      .filter((hit): hit is Track => 'type' in hit && hit.type === 'track')
      .map(hit => {
        const track = normalizeV2Track(hit) as Track
        return track.source ? serializeAggregatedTrack(track) : track
      })

    if (tracks.length === 0) return tracks
    dispatch({ type: TRACKS_UPDATE, tracks })

    if (getConstanceSetting(getState(), NESTED_TRACKS_IN_SEARCH)) {
      const mainTrackIds = tracks
        .filter(track => getNestedTracksCount(track) > 0)
        .map(track => track.id)
      if (mainTrackIds.length > 0) {
        dispatch(
          fetchNestedTracks(mainTrackIds, { includePlaylistsCount: true }),
        )
      }
    }

    return tracks
  }
